'use client';

import { c as _c } from "react/compiler-runtime";
import { CircleHelp, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { Components } from 'react-markdown';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { LogoGlyph } from '@/components/common/LogoGlyph';
import type { StreamdownAnchorTransform } from '@/components/markdown/StreamdownRenderer';
import { SummarizerStreamingText } from '@/components/summarizer/SummarizerStreamingText';
import { addLineBreakAfterHandleAnchors } from '@/components/summarizer/summaryTextUtils';
import type { StreamStatus } from '@/components/tweet/summary/types';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
interface SummarizerSummaryCardProperties {
  afterContent?: ReactNode | undefined;
  chatLabel?: string | undefined;
  chatLoadingLabel?: string | undefined;
  className?: string | undefined;
  components?: Components | undefined;
  dismissTooltip?: string | undefined;
  emptyLabel?: string | undefined;
  error?: string | null | undefined;
  generatingLabel?: string | undefined;
  helpContent?: ReactNode | undefined;
  isChatEnabled?: boolean | undefined;
  isChatLoading?: boolean | undefined;
  isRetryDisabled?: boolean | undefined;
  leadingActions?: ReactNode | undefined;
  markdown?: boolean | undefined;
  onChat?: (() => void) | undefined;
  onDismiss?: (() => void) | undefined;
  onRetry?: (() => void) | undefined;
  retryLabel?: string | undefined;
  status: StreamStatus;
  statusMessage?: string | null | undefined;
  text: string;
  textClassName?: string | undefined;
  textWrapperClassName?: string | undefined;
  title?: string | undefined;
  trailingActions?: ReactNode | undefined;
  transformAnchor?: StreamdownAnchorTransform | undefined;
}
export function SummarizerSummaryCard(t0) {
  const $ = _c(69);
  const {
    status,
    text,
    error,
    title: t1,
    className,
    markdown: t2,
    generatingLabel: t3,
    statusMessage,
    emptyLabel: t4,
    onChat,
    isChatEnabled: t5,
    isChatLoading: t6,
    chatLabel: t7,
    chatLoadingLabel: t8,
    helpContent,
    dismissTooltip: t9,
    onDismiss,
    leadingActions,
    trailingActions,
    afterContent,
    onRetry,
    retryLabel: t10,
    isRetryDisabled: t11,
    transformAnchor,
    components,
    textClassName: t12,
    textWrapperClassName
  } = t0;
  const title = t1 === undefined ? "Summary" : t1;
  const markdown = t2 === undefined ? false : t2;
  const generatingLabel = t3 === undefined ? "Generating summary\u2026" : t3;
  const emptyLabel = t4 === undefined ? "No summary available yet." : t4;
  const isChatEnabled = t5 === undefined ? true : t5;
  const isChatLoading = t6 === undefined ? false : t6;
  const chatLabel = t7 === undefined ? "Chat" : t7;
  const chatLoadingLabel = t8 === undefined ? "Chatting\u2026" : t8;
  const dismissTooltip = t9 === undefined ? "Hide summary" : t9;
  const retryLabel = t10 === undefined ? "Try again" : t10;
  const isRetryDisabled = t11 === undefined ? false : t11;
  const textClassName = t12 === undefined ? "leading-relaxed text-card-foreground break-words" : t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  if ($[0] !== chatLabel || $[1] !== chatLoadingLabel || $[2] !== className || $[3] !== components || $[4] !== dismissTooltip || $[5] !== emptyLabel || $[6] !== error || $[7] !== generatingLabel || $[8] !== helpContent || $[9] !== isChatEnabled || $[10] !== isChatLoading || $[11] !== isRetryDisabled || $[12] !== leadingActions || $[13] !== markdown || $[14] !== onChat || $[15] !== onDismiss || $[16] !== onRetry || $[17] !== retryLabel || $[18] !== status || $[19] !== statusMessage || $[20] !== text || $[21] !== textClassName || $[22] !== textWrapperClassName || $[23] !== title || $[24] !== trailingActions || $[25] !== transformAnchor) {
    const normalizedText = markdown ? addLineBreakAfterHandleAnchors(text) : text;
    const trimmedText = normalizedText.trim();
    const hasText = trimmedText.length > 0;
    const isStreaming = status === "loading" || status === "streaming";
    const showChatButton = Boolean(onChat) && isChatEnabled && hasText;
    const showHelp = Boolean(helpContent);
    const showDismiss = Boolean(onDismiss);
    const showActions = showChatButton || showHelp || showDismiss || leadingActions || trailingActions;
    const resolvedTextClassName = cn(textClassName, markdown ? null : "whitespace-pre-line");
    let t18;
    bb0: {
      if (!markdown) {
        t18 = components;
        break bb0;
      }
      let t19;
      if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = {
          p: _temp,
          ul: _temp2,
          ol: _temp3,
          li: _temp4,
          h1: _temp5,
          h2: _temp6,
          h3: _temp7
        };
        $[31] = t19;
      } else {
        t19 = $[31];
      }
      const tightComponents = t19;
      let t20;
      if ($[32] !== components) {
        t20 = components ? {
          ...tightComponents,
          ...components
        } : tightComponents;
        $[32] = components;
        $[33] = t20;
      } else {
        t20 = $[33];
      }
      t18 = t20;
    }
    const markdownComponents = t18;
    const renderBody = () => {
      if (status === "error") {
        const canRetryOrExplain = Boolean(error || onRetry);
        if (!canRetryOrExplain) {
          return <p className="text-sm text-destructive">Unable to summarize.</p>;
        }
        return <div className="flex flex-col gap-2 text-sm text-destructive"><span>{error ?? "Unable to summarize."}</span>{onRetry ? <Button size="sm" variant="outline" className="self-start" onClick={onRetry} disabled={isRetryDisabled}>{retryLabel}</Button> : null}</div>;
      }
      if (hasText) {
        const streamingProps = {
          text: trimmedText,
          status,
          markdown,
          className: resolvedTextClassName
        };
        if (textWrapperClassName) {
          streamingProps.wrapperClassName = textWrapperClassName;
        }
        if (transformAnchor !== undefined) {
          streamingProps.transformAnchor = transformAnchor;
        }
        if (markdownComponents) {
          streamingProps.components = markdownComponents;
        }
        return <SummarizerStreamingText {...streamingProps} />;
      }
      if (status === "complete") {
        return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
      }
      const generatingCopy = statusMessage ?? generatingLabel;
      return <p className="text-sm text-muted-foreground">{generatingCopy}</p>;
    };
    if ($[34] !== className) {
      t15 = cn("rounded-lg border border-border/60 bg-card p-3 text-sm text-card-foreground", className);
      $[34] = className;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    t16 = true;
    let t19;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <LogoGlyph size={14} />;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    let t20;
    if ($[37] !== title) {
      t20 = <span>{title}</span>;
      $[37] = title;
      $[38] = t20;
    } else {
      t20 = $[38];
    }
    let t21;
    if ($[39] !== isStreaming) {
      t21 = isStreaming ? <CuteSpinner size={14} className="border-current/30 border-t-current" aria-hidden="true" /> : null;
      $[39] = isStreaming;
      $[40] = t21;
    } else {
      t21 = $[40];
    }
    let t22;
    if ($[41] !== chatLabel || $[42] !== chatLoadingLabel || $[43] !== dismissTooltip || $[44] !== helpContent || $[45] !== isChatEnabled || $[46] !== isChatLoading || $[47] !== leadingActions || $[48] !== onChat || $[49] !== onDismiss || $[50] !== showActions || $[51] !== showChatButton || $[52] !== showDismiss || $[53] !== showHelp || $[54] !== trailingActions) {
      t22 = showActions ? <div className="ml-auto flex items-center gap-1 text-muted-foreground">{leadingActions}{showChatButton ? <Button type="button" size="sm" variant="ghost" className={cn("h-7 rounded-lg px-3 text-xs font-semibold uppercase tracking-[0.18em] text-primary transition-colors hover:bg-primary/15 hover:text-primary focus-visible:ring-primary/45 focus-visible:ring-offset-0 disabled:text-primary/40 disabled:opacity-100", isChatLoading && "bg-primary/15")} onClick={onChat} disabled={!isChatEnabled || isChatLoading}>{isChatLoading ? <><CuteSpinner size={12} className="mr-2 border-current/30 border-t-current" aria-hidden="true" />{chatLoadingLabel}</> : chatLabel}</Button> : null}{showHelp ? <TooltipProvider delayDuration={150} skipDelayDuration={300}><Tooltip><TooltipTrigger asChild={true}><button type="button" className="rounded-full p-1 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30" aria-label="View summary details"><CircleHelp className="h-4 w-4" aria-hidden="true" /></button></TooltipTrigger><TooltipContent side="top" align="end" className="max-w-xs whitespace-pre-line text-xs">{helpContent}</TooltipContent></Tooltip></TooltipProvider> : null}{showDismiss ? <TooltipProvider delayDuration={150} skipDelayDuration={300}><Tooltip><TooltipTrigger asChild={true}><button type="button" className="rounded-full p-1 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30" onClick={onDismiss} aria-label={dismissTooltip}><X className="h-4 w-4" aria-hidden="true" /></button></TooltipTrigger><TooltipContent side="top" align="end" className="max-w-xs text-xs">{dismissTooltip}</TooltipContent></Tooltip></TooltipProvider> : null}{trailingActions}</div> : null;
      $[41] = chatLabel;
      $[42] = chatLoadingLabel;
      $[43] = dismissTooltip;
      $[44] = helpContent;
      $[45] = isChatEnabled;
      $[46] = isChatLoading;
      $[47] = leadingActions;
      $[48] = onChat;
      $[49] = onDismiss;
      $[50] = showActions;
      $[51] = showChatButton;
      $[52] = showDismiss;
      $[53] = showHelp;
      $[54] = trailingActions;
      $[55] = t22;
    } else {
      t22 = $[55];
    }
    if ($[56] !== t20 || $[57] !== t21 || $[58] !== t22) {
      t17 = <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary">{t19}{t20}{t21}{t22}</div>;
      $[56] = t20;
      $[57] = t21;
      $[58] = t22;
      $[59] = t17;
    } else {
      t17 = $[59];
    }
    t13 = "mt-2.5 space-y-2";
    t14 = renderBody();
    $[0] = chatLabel;
    $[1] = chatLoadingLabel;
    $[2] = className;
    $[3] = components;
    $[4] = dismissTooltip;
    $[5] = emptyLabel;
    $[6] = error;
    $[7] = generatingLabel;
    $[8] = helpContent;
    $[9] = isChatEnabled;
    $[10] = isChatLoading;
    $[11] = isRetryDisabled;
    $[12] = leadingActions;
    $[13] = markdown;
    $[14] = onChat;
    $[15] = onDismiss;
    $[16] = onRetry;
    $[17] = retryLabel;
    $[18] = status;
    $[19] = statusMessage;
    $[20] = text;
    $[21] = textClassName;
    $[22] = textWrapperClassName;
    $[23] = title;
    $[24] = trailingActions;
    $[25] = transformAnchor;
    $[26] = t13;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
    $[30] = t17;
  } else {
    t13 = $[26];
    t14 = $[27];
    t15 = $[28];
    t16 = $[29];
    t17 = $[30];
  }
  let t18;
  if ($[60] !== afterContent || $[61] !== t13 || $[62] !== t14) {
    t18 = <div className={t13}>{t14}{afterContent}</div>;
    $[60] = afterContent;
    $[61] = t13;
    $[62] = t14;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== t15 || $[65] !== t16 || $[66] !== t17 || $[67] !== t18) {
    t19 = <div className={t15} data-no-conversation={t16}>{t17}{t18}</div>;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
  } else {
    t19 = $[68];
  }
  return t19;
}
function _temp7(t0) {
  const {
    className: incomingClassName_5,
    children: children_5,
    ...properties_5
  } = t0;
  return <h3 className={cn("mt-6 mb-3 text-sm font-semibold leading-tight first:mt-0", incomingClassName_5)} {...properties_5}>{children_5}</h3>;
}
function _temp6(t0) {
  const {
    className: incomingClassName_4,
    children: children_4,
    ...properties_4
  } = t0;
  return <h2 className={cn("mt-6 mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground first:mt-0", incomingClassName_4)} {...properties_4}>{children_4}</h2>;
}
function _temp5(t0) {
  const {
    className: incomingClassName_3,
    children: children_3,
    ...properties_3
  } = t0;
  return <h1 className={cn("mb-3 text-base font-semibold leading-tight last:mb-0", incomingClassName_3)} {...properties_3}>{children_3}</h1>;
}
function _temp4(t0) {
  const {
    className: incomingClassName_2,
    children: children_2,
    ...properties_2
  } = t0;
  return <li className={cn("leading-relaxed", incomingClassName_2)} {...properties_2}>{children_2}</li>;
}
function _temp3(t0) {
  const {
    className: incomingClassName_1,
    children: children_1,
    ...properties_1
  } = t0;
  return <ol className={cn("mb-3 list-decimal space-y-2 pl-4 last:mb-0", incomingClassName_1)} {...properties_1}>{children_1}</ol>;
}
function _temp2(t0) {
  const {
    className: incomingClassName_0,
    children: children_0,
    ...properties_0
  } = t0;
  return <ul className={cn("mb-3 list-disc space-y-2 pl-4 last:mb-0", incomingClassName_0)} {...properties_0}>{children_0}</ul>;
}
function _temp(t0) {
  const {
    className: incomingClassName,
    children,
    ...properties
  } = t0;
  return <p className={cn("mb-3 last:mb-0 leading-relaxed", incomingClassName)} {...properties}>{children}</p>;
}
export type { SummarizerSummaryCardProperties as SummarizerSummaryCardProps };